// eslint-disable-next-line import/no-extraneous-dependencies
import axios from 'axios';
import configService from '@/services/remoteConfig';

// API endpoint for backend - get from Remote Config
const url = configService.get('apiBaseUrl');
const apiClient = axios.create({
  baseURL: url,
});

// Get Discogs tokens from localStorage
const getDiscogsTokens = () => {
  try {
    const authData = JSON.parse(localStorage.getItem('discogs_auth'));
    if (!authData || !authData.accessToken || !authData.accessTokenSecret) {
      return null;
    }
    return {
      accessToken: authData.accessToken,
      accessTokenSecret: authData.accessTokenSecret,
    };
  } catch (error) {
    console.error('Error getting Discogs tokens from localStorage:', error);
    return null;
  }
};

// Send Discogs tokens as headers, never in the URL, so they stay out of logs and history
apiClient.interceptors.request.use(config => {
  const tokens = getDiscogsTokens();
  if (tokens) {
    config.headers.set('X-Discogs-Token', tokens.accessToken);
    config.headers.set('X-Discogs-Token-Secret', tokens.accessTokenSecret);
  }
  return config;
}, error => Promise.reject(error));

// A 401 means Discogs no longer accepts the stored session: drop it and send the user to log in,
// remembering where they were
apiClient.interceptors.response.use(response => response, error => {
  if (error.response?.status === 401) {
    try {
      localStorage.removeItem('discogs_auth');
      localStorage.removeItem('collection_cache_v2');
    } catch (storageError) {
      // Nothing stored
    }
    const here = `${window.location.pathname}${window.location.search}`;
    if (!here.startsWith('/login')) window.location.replace(`/login?redirect=${encodeURIComponent(here)}`);
  }
  return Promise.reject(error);
});

/**
 * Fetches the URI for a given Discogs master ID from the backend API.
 * @param {string|number} masterId - The master ID to look up.
 * @returns {Promise<Object>} The URI data from the backend.
 */
const getUriFromMaster = async masterId => {
  try {
    const tokens = getDiscogsTokens();
    if (!tokens) {
      throw new Error('Authentication required to access master information');
    }

    const { data } = await apiClient.get(`getMasterUrl/${masterId}`);
    return data;
  } catch (error) {
    console.error('Error fetching master URL:', error);
    throw error;
  }
};

export const searchCollection = async (searchQuery, page = 1, limit = 10, searchUserCollection = true) => {
  // Build query parameters
  const params = {
    q: searchQuery,
    page: page.toString(),
    limit: limit.toString(),
    searchUserCollection: searchUserCollection.toString(),
  };

  const tokens = getDiscogsTokens();
  if (searchUserCollection && !tokens) {
    throw new Error('Authentication required to search your collection');
  }

  // Make the request (auth tokens are added by the interceptor)
  try {
    const { data } = await apiClient.get('searchCollection', { params });
    return data;
  } catch (error) {
    console.error('Error searching collection:', error);
    throw error;
  }
};

/**
 * Fetches the whole collection with tags read from the user's custom collection fields.
 * @param {Object} options - { refresh: true } skips the server's cached copy
 * @returns {Promise<Object>} { username, releases }
 */
const getTaggedCollection = async ({ refresh = false } = {}) => {
  try {
    const { data } = await apiClient.get('getTaggedCollection', { params: refresh ? { refresh: true } : {} });
    return data;
  } catch (error) {
    console.error('Error fetching tagged collection:', error);
    throw error;
  }
};

/**
 * Asks the backend for suggested genre tags and an original year for a release. Writes nothing.
 * @returns {Promise<Object>} { genres, confidence, reason, originalYear, options }
 */
const suggestTags = async releaseId => {
  const { data } = await apiClient.get('suggestTags', { params: { releaseId } });
  return data;
};

/**
 * Writes tags for one record to Discogs (and files it in its genre folder)
 * @param {Object} payload - { instanceId, releaseId, folderId, genres?, originalYear? }
 * @returns {Promise<Object>} { folderId, genres, originalYear } as written
 */
const applyTags = async payload => {
  const { data } = await apiClient.post('applyTags', payload);
  return data;
};

export {
  suggestTags,
  applyTags,
  getTaggedCollection,
  getUriFromMaster,
};
